<script lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-svelte';
import { carousel, type EmblaCarouselType } from '$lib/actions/carousel.js';
import { stripSiteDomain } from '$lib/utils/url.js';
import Link from '../../atoms/Link/Link.svelte';
import BlockHeader from '../BlockHeader/BlockHeader.svelte';

type TTestimonial = {
    quote: string;
    person: string;
    role: string;
    company: string;
    to: string;
    linkedIn?: string;
    caseStudy?: string;
};

type TTestimonialRef = {
    testimonial?: {
        value?: {
            data?: TTestimonial & { description?: string };
        };
        // Raw (unenriched) reference format used by the Builder editor
        '@type'?: string;
        model?: string;
        id?: string;
    };
};

type TBuilderTestimonialResponse = {
    data?: TTestimonial & { description?: string };
};

interface Props {
    header?: string;
    ctaTo?: string;
    ctaText?: string;
    testimonials?: (TTestimonial | TTestimonialRef)[];
    hideBlock?: boolean;
}

const BUILDER_API_KEY = '5d0546160e954b87b82e2663451afcb1';

let {
    header = '',
    ctaTo = '',
    ctaText = '',
    testimonials = [],
    hideBlock = false,
}: Props = $props();

// Resolved testimonials including any fetched from raw references
let resolvedTestimonials = $state<(TTestimonial | TTestimonialRef)[]>([]);

function normaliseTestimonial(item: TTestimonial | TTestimonialRef): TTestimonial | null {
    if ('testimonial' in item && item.testimonial?.value?.data) {
        const { description, ...rest } = item.testimonial.value.data;
        return rest as TTestimonial;
    }
    if ('quote' in item && item.quote) {
        return item as TTestimonial;
    }
    return null;
}

/**
 * Check if an item is a raw (unenriched) Builder reference.
 * The Builder editor sends references in this format before enrichment.
 */
function isRawReference(item: TTestimonial | TTestimonialRef): string | null {
    if ('testimonial' in item && item.testimonial) {
        const ref = item.testimonial;
        if (ref['@type'] === '@builder.io/core:Reference' && ref.id) {
            return ref.id;
        }
    }
    return null;
}

/**
 * Fetch a single testimonial entry by ID from the Builder Content API
 */
async function fetchTestimonialEntry(id: string): Promise<TTestimonialRef | null> {
    try {
        const url = `https://cdn.builder.io/api/v3/content/testimonials/${id}?apiKey=${BUILDER_API_KEY}&cachebust=${Date.now()}`;
        const res = await fetch(url);
        const entry: TBuilderTestimonialResponse = await res.json();
        if (entry?.data) {
            return { testimonial: { value: { data: entry.data } } };
        }
    } catch {
        // Silently fail — testimonial just won't render
    }
    return null;
}

/**
 * Resolve raw references by fetching their data client-side.
 * Only needed in the Builder editor where references aren't enriched.
 */
async function resolveReferences(items: (TTestimonial | TTestimonialRef)[]) {
    const resolved = await Promise.all(
        items.map(async (item) => {
            const rawId = isRawReference(item);
            if (rawId) {
                const fetched = await fetchTestimonialEntry(rawId);
                return fetched ?? item;
            }
            return item;
        }),
    );
    resolvedTestimonials = resolved;
}

$effect(() => {
    const hasRawRefs = testimonials.some((item) => isRawReference(item) !== null);
    if (hasRawRefs) {
        resolveReferences(testimonials);
    } else {
        resolvedTestimonials = testimonials;
    }
});

// Transform URLs in testimonials array from Builder.io
const transformedTestimonials = $derived(
    resolvedTestimonials
        .map(normaliseTestimonial)
        .filter((t): t is TTestimonial => t !== null)
        .map((testimonial) => ({
            ...testimonial,
            to: stripSiteDomain(testimonial.to),
            linkedIn: stripSiteDomain(testimonial.linkedIn),
            caseStudy: stripSiteDomain(testimonial.caseStudy),
        })),
);

let currentSlide = $state(0);
let emblaApi: EmblaCarouselType | null = $state(null);
const hasMultipleSlides = $derived(transformedTestimonials.length > 1);
</script>

{#if !hideBlock}
    <BlockHeader {header} {ctaTo} {ctaText} withPageMargins />
    <div class="testimonials">
        <div
            class="testimonials__container"
            use:carousel={{
                ariaLabel: 'Customer testimonials',
                slideCount: transformedTestimonials.length,
                onInit: (api) => { emblaApi = api; },
                onSelect: (index) => { currentSlide = index; },
            }}
        >
            <div class="testimonials__viewport" data-carousel-viewport>
                <div class="testimonials__slides">
                    {#each transformedTestimonials as testimonial}
                            <div class="testimonial" data-carousel-slide>
                                {#if testimonial.quote}
                                    <blockquote class="quote">
                                        {testimonial.quote}
                                    </blockquote>
                                {/if}

                                <div class="cite">
                                    <div class="person">
                                        {#if testimonial.linkedIn}
                                            <Link to={testimonial.linkedIn} class="link">
                                                {#snippet text()}{testimonial.person}{/snippet}
                                            </Link>
                                        {:else}
                                            {testimonial.person}
                                        {/if}
                                    </div>

                                    {#if testimonial.role}
                                        <div class="role">{testimonial.role}</div>
                                    {/if}

                                    {#if testimonial.company}
                                        <div class="company">
                                            <Link to={testimonial.to} class="link">
                                                {#snippet text()}
                                                    {testimonial.company}
                                                {/snippet}
                                            </Link>
                                        </div>
                                    {/if}

                                    {#if testimonial.caseStudy}
                                        <div class="case-study">
                                            <Link to={testimonial.caseStudy} class="case-study__link">
                                                {#snippet text()}View case study <span class="case-study__arrow" aria-hidden="true">→</span>{/snippet}
                                            </Link>
                                        </div>
                                    {/if}
                                </div>
                            </div>
                    {/each}
                </div>
            </div>

            {#if hasMultipleSlides}
                <div class="carousel-nav" role="navigation" aria-label="Testimonial navigation">
                    <button
                        class="carousel-arrow"
                        aria-label="Previous testimonial"
                        onclick={() => emblaApi?.scrollPrev()}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div class="carousel-dots">
                        {#each transformedTestimonials as testimonial, index}
                            <button
                                class="carousel-dot"
                                class:carousel-dot--active={currentSlide === index}
                                aria-current={currentSlide === index ? 'step' : undefined}
                                aria-label="Go to testimonial {index + 1}: {testimonial.person}"
                                onclick={() => emblaApi?.scrollTo(index)}
                            ></button>
                        {/each}
                    </div>
                    <button
                        class="carousel-arrow"
                        aria-label="Next testimonial"
                        onclick={() => emblaApi?.scrollNext()}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            {/if}
        </div>
    </div>
{/if}

<style lang="scss">
    @use "/src/scss/global-constants/typography";
    @use "/src/scss/global-constants/fonts";
    @use "/src/scss/components/carousel" as carousel;
    @use "sass-mq/mq" as *;

    .testimonials {
        background-color: var(--colour-classic-chalk);
        margin: 0;
        overflow: hidden;
    }

    .testimonials__viewport {
        overflow: hidden;
    }

    .testimonials__slides {
        display: flex;
    }

    .testimonial {
        flex: 0 0 100%;
        min-width: 0;
        padding: 8rem 1.3rem;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .quote {
        color: var(--colour-dreamless-sleep);
        display: inline-block;
        margin-bottom: 2rem;
        max-width: 90rem;
        position: relative;
        text-wrap: balance;
        font-style: italic;
        @include typography.text-quote;
        line-height: 1.4;
        white-space: break-spaces;

        @include mq($from: tablet) {
            margin-bottom: 4rem;
        }

        &::before {
            content: "\201C";
        }

        &::after {
            content: "\201D";
        }
    }

    .cite {
        color: var(--colour-squant);
        @include typography.text-caption;
    }

    .person {
        color: var(--colour-dreamless-sleep);
        font-weight: fonts.$fonts-weight--medium;
        margin-bottom: 0.8rem;
    }

    .case-study {
        margin-top: 1.2rem;
        @include typography.label-sm;
    }

    :global(.case-study__link) {
        color: var(--colour-squant);
        text-decoration: none;
        letter-spacing: 0.04em;
        transition: color 0.2s ease;

        &:hover {
            color: var(--colour-dreamless-sleep);

            :global(.case-study__arrow) {
                translate: 0.25em 0;
            }
        }
    }

    :global(.case-study__arrow) {
        display: inline-block;
        transition: translate 0.2s ease;
    }

    .carousel-nav {
        @include carousel.nav;
        padding-bottom: 2rem;
    }

    .carousel-arrow {
        @include carousel.arrow-base;
        color: var(--colour-dreamless-sleep);
    }

    .carousel-dots {
        @include carousel.dots;
    }

    .carousel-dot {
        @include carousel.dot;
    }

    .carousel-dot--active {
        @include carousel.dot-active;
    }
</style>
